import type {
  AdminPartnerCategoryGroup,
  AdminPartnerCategoryTag,
} from "@/types/admin-partner-category-group";

/** Languages of the group name form; English is required, the rest optional. */
export const CATEGORY_GROUP_LOCALES = [
  "en",
  "es",
  "de",
  "fr",
  "it",
  "pt",
  "nl",
  "ru",
  "uk",
] as const;

export type CategoryGroupLocale = (typeof CATEGORY_GROUP_LOCALES)[number];

export type CategoryGroupNamesForm = Record<CategoryGroupLocale, string>;

/** A provider tag with the group it is in; `groupId` null means "Other". */
export type CategoryTagEntry = {
  tagId: number;
  name: string;
  groupId: string | null;
  groupName: string | null;
};

/** Every tag once: the tags of the groups and those outside them, by name. */
export function buildCategoryTagCatalog(
  groups: readonly AdminPartnerCategoryGroup[],
  unassigned: readonly AdminPartnerCategoryTag[],
): CategoryTagEntry[] {
  const entries = new Map<number, CategoryTagEntry>();
  for (const group of groups) {
    for (const tag of group.tags) {
      if (!entries.has(tag.tagId)) {
        entries.set(tag.tagId, {
          tagId: tag.tagId,
          name: tag.name,
          groupId: group.id,
          groupName: group.name,
        });
      }
    }
  }
  for (const tag of unassigned) {
    if (!entries.has(tag.tagId)) {
      entries.set(tag.tagId, {
        tagId: tag.tagId,
        name: tag.name,
        groupId: null,
        groupName: null,
      });
    }
  }
  return [...entries.values()].sort(
    (left, right) =>
      left.name.localeCompare(right.name) || left.tagId - right.tagId,
  );
}

/** Tags whose name or id contains the query. */
export function searchCategoryTags(
  catalog: readonly CategoryTagEntry[],
  query: string,
): CategoryTagEntry[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [...catalog];
  return catalog.filter(
    (entry) =>
      entry.name.toLowerCase().includes(needle) ||
      String(entry.tagId).includes(needle),
  );
}

export function groupTagIdsWith(
  group: AdminPartnerCategoryGroup,
  tagId: number,
): number[] {
  const tagIds = group.tags.map((tag) => tag.tagId);
  return tagIds.includes(tagId) ? tagIds : [...tagIds, tagId];
}

export function groupTagIdsWithout(
  group: AdminPartnerCategoryGroup,
  tagId: number,
): number[] {
  return group.tags.map((tag) => tag.tagId).filter((id) => id !== tagId);
}

/** Adding the tag to `groupId` takes it out of the group it is in now. */
export function movesFromAnotherGroup(
  groupId: string,
  entry: CategoryTagEntry,
): boolean {
  return entry.groupId !== null && entry.groupId !== groupId;
}

/** The filled languages of the form, trimmed. */
export function namesFromForm(
  form: Partial<Record<string, string>>,
): Record<string, string> {
  const names: Record<string, string> = {};
  for (const [locale, value] of Object.entries(form)) {
    const trimmed = value?.trim();
    if (trimmed) names[locale] = trimmed;
  }
  return names;
}

export function namesToForm(
  names: Record<string, string> | undefined,
): CategoryGroupNamesForm {
  return Object.fromEntries(
    CATEGORY_GROUP_LOCALES.map((locale) => [locale, names?.[locale] ?? ""]),
  ) as CategoryGroupNamesForm;
}

export function hasEnglishName(names: Record<string, string>): boolean {
  return !!names.en?.trim();
}
